import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './App';
import { 
  MessageSquare, Users, Mail, Crown, Calendar, Clock, 
  TrendingUp, Activity, ChevronDown, ChevronUp, Eye,
  MessageCircle, User, Zap, BarChart3, PieChart, Download
} from 'lucide-react';

function AdminChatUsage() {
  const [stats, setStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchChatStats();
  }, [period]);

  const fetchChatStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/chat-usage`, {
        params: { period, limit: 20 },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch chat stats:', error);
      setMessage('Failed to load chat statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserChatDetails = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${userId}/chat-usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUserDetails(response.data);
        setSelectedUser(userId);
      }
    } catch (error) {
      console.error('Failed to fetch user chat details:', error);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num > 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num > 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-400 border-t-transparent"></div>
        <p className="text-gray-400 mt-4">Loading chat statistics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400">{message}</p>
        </div>
      )}

      {/* Period Filter */}
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
        <div className="flex items-center space-x-4">
          <Calendar className="w-5 h-5 text-gray-500" />
          <span className="text-white">Time Period:</span>
          <div className="flex space-x-2">
            {[
              { value: 'today', label: 'Today' },
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'all', label: 'All Time' }
            ].map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  period === p.value 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {stats && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Chats</p>
                  <p className="text-3xl font-bold text-white">{formatNumber(stats.overall.total_chats)}</p>
                  <p className="text-xs text-blue-400 mt-1">{stats.overall.active_users} active users</p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Messages</p>
                  <p className="text-3xl font-bold text-white">{formatNumber(stats.overall.total_messages)}</p>
                  <p className="text-xs text-green-400 mt-1">
                    {stats.overall.user_messages} user · {stats.overall.assistant_messages} AI
                  </p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-lg">
                  <MessageCircle className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Avg. Messages/Chat</p>
                  <p className="text-3xl font-bold text-white">{stats.overall.avg_messages_per_chat}</p>
                  <p className="text-xs text-purple-400 mt-1">
                    Response time: {formatTime(stats.overall.avg_response_time)}
                  </p>
                </div>
                <div className="p-3 bg-purple-500/20 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Inactive Users</p>
                  <p className="text-3xl font-bold text-white">{formatNumber(stats.inactive_users)}</p>
                  <p className="text-xs text-yellow-400 mt-1">Never used chat</p>
                </div>
                <div className="p-3 bg-yellow-500/20 rounded-lg">
                  <Users className="w-6 h-6 text-yellow-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Top Users by Chat Activity */}
          <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-purple-400" />
                <span>Most Active Chat Users</span>
              </h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700 bg-gray-900/50">
                    <th className="py-4 px-4 text-left text-gray-400 text-sm">User</th>
                    <th className="py-4 px-4 text-left text-gray-400 text-sm">Plan</th>
                    <th className="py-4 px-4 text-left text-gray-400 text-sm">Chats</th>
                    <th className="py-4 px-4 text-left text-gray-400 text-sm">Messages</th>
                    <th className="py-4 px-4 text-left text-gray-400 text-sm">User/AI Ratio</th>
                    <th className="py-4 px-4 text-left text-gray-400 text-sm">Last Active</th>
                    <th className="py-4 px-4 text-left text-gray-400 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_users.map((user, index) => (
                    <React.Fragment key={user.id}>
                      <tr className="border-b border-gray-700/50 hover:bg-gray-800/30">
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-6 h-6 flex items-center justify-center bg-gray-700 rounded-full text-xs font-bold text-white">
                              {index + 1}
                            </span>
                            <div>
                              <p className="text-white text-sm font-medium">{user.email}</p>
                              <p className="text-gray-500 text-xs">ID: {user.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {user.plan === 'paid' ? (
                            <div className="flex items-center space-x-1">
                              <Crown className="w-4 h-4 text-yellow-400" />
                              <span className="text-yellow-400 text-xs">Premium</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">Free</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-white font-medium">{user.chat_count}</p>
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-white font-medium">{user.message_count}</p>
                          <p className="text-gray-500 text-xs">
                            {user.user_messages} user / {user.message_count - user.user_messages} AI
                          </p>
                        </td>
                        <td className="py-4 px-4">
                          <div className="w-24 bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-purple-500 h-2 rounded-full"
                              style={{ width: `${(user.user_messages / user.message_count * 100) || 0}%` }}
                            ></div>
                          </div>
                          <p className="text-gray-400 text-xs mt-1">
                            {Math.round((user.user_messages / user.message_count * 100)) || 0}% user
                          </p>
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-white text-sm">{new Date(user.last_active).toLocaleDateString()}</p>
                          <p className="text-gray-500 text-xs">
                            {new Date(user.last_active).toLocaleTimeString()}
                          </p>
                        </td>
                        <td className="py-4 px-4">
                          <button
                            onClick={() => fetchUserChatDetails(user.id)}
                            className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                      
                      {/* User Details */}
                      {selectedUser === user.id && userDetails && userDetails.user_id === user.id && (
                        <tr className="bg-gray-900/50">
                          <td colSpan="7" className="p-6">
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h3 className="text-white font-semibold">Chat History Details</h3>
                                <button
                                  onClick={() => setSelectedUser(null)}
                                  className="text-gray-400 hover:text-white"
                                >
                                  <ChevronUp className="w-5 h-5" />
                                </button>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-gray-800/50 rounded-lg p-4">
                                  <p className="text-gray-400 text-xs">Total Chats</p>
                                  <p className="text-2xl font-bold text-white">{userDetails.stats.total_chats || 0}</p>
                                </div>
                                <div className="bg-gray-800/50 rounded-lg p-4">
                                  <p className="text-gray-400 text-xs">Total Messages</p>
                                  <p className="text-2xl font-bold text-white">{userDetails.stats.total_messages || 0}</p>
                                </div>
                                <div className="bg-gray-800/50 rounded-lg p-4">
                                  <p className="text-gray-400 text-xs">Avg/Chat</p>
                                  <p className="text-2xl font-bold text-white">
                                    {Math.round(userDetails.stats.avg_messages_per_chat * 10) / 10 || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-800/50 rounded-lg p-4">
                                  <p className="text-gray-400 text-xs">First Chat</p>
                                  <p className="text-white text-sm">
                                    {userDetails.stats.first_chat 
                                      ? new Date(userDetails.stats.first_chat).toLocaleDateString()
                                      : 'Never'}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Recent Chats */}
                              {userDetails.recent_chats && userDetails.recent_chats.length > 0 && (
                                <div>
                                  <p className="text-white text-sm font-medium mb-2">Recent Chat Sessions</p>
                                  <div className="space-y-2">
                                    {userDetails.recent_chats.map(chat => (
                                      <div key={chat.id} className="bg-gray-800/30 rounded-lg p-3 flex items-center justify-between">
                                        <div>
                                          <p className="text-white text-sm">{chat.title}</p>
                                          <p className="text-gray-400 text-xs">
                                            {chat.message_count} messages · {Math.round(chat.storage_bytes / 1024)} KB
                                          </p>
                                        </div>
                                        <p className="text-gray-500 text-xs">
                                          {new Date(chat.created_at).toLocaleDateString()}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Daily Activity Summary */}
          <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
              <Activity className="w-5 h-5 text-green-400" />
              <span>Daily Chat Activity (Last 30 Days)</span>
            </h2>
            
            <div className="space-y-3">
              {stats.daily_activity.slice(0, 10).map((day) => (
                <div key={day.date} className="flex items-center space-x-4">
                  <span className="text-gray-400 text-sm w-24">{new Date(day.date).toLocaleDateString()}</span>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${(day.chats / Math.max(...stats.daily_activity.map(d => d.chats)) * 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-white text-sm w-16">{day.chats} chats</span>
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="flex-1 bg-gray-700 rounded-full h-1.5">
                        <div 
                          className="bg-green-500 h-1.5 rounded-full"
                          style={{ width: `${(day.messages / Math.max(...stats.daily_activity.map(d => d.messages)) * 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-gray-400 text-xs w-16">{day.messages} msgs</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminChatUsage;